import classnames from 'classnames'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft, faAngleRight, faPlus } from '@fortawesome/pro-light-svg-icons'

import { Box } from '@domain-group/suit-theme'

import type {
  UsePublicationBookingPlannerPayload,
} from '@components/PublicationBookingPlanner/hooks/usePublicationBookingPlanner'

import useCanvas from '@components/PublicationBookingPlanner/hooks/useCanvas'

import type { PublicationBookingModel } from '@models/types'

import ContentGrid from './ContentGrid'

import styles from './styles.module.scss'

type RenderAddPageButtonParams = {
  index: number,
  isBooked: boolean,
  isPending: boolean,
  publicationBookings: PublicationBookingModel[],
  showCreateOrEditPublicationBookingModal: UsePublicationBookingPlannerPayload['callbacks']['showCreateOrEditPublicationBookingModal'],
}

const renderAddPageButton = (params: RenderAddPageButtonParams) => {
  const {
    index,
    isPending,
    isBooked,
    publicationBookings,
    showCreateOrEditPublicationBookingModal,
  } = params

  const lastBooking = index === publicationBookings.length - 1
  if (isPending && !isBooked && lastBooking){
    return (
      <a
        className={classnames(styles.navigation, styles.add)}
        onClick={showCreateOrEditPublicationBookingModal}
      >
        <FontAwesomeIcon icon={faPlus} />
      </a>
    )
  }

  return null
}

type RenderPageNavigationButtonParams = {
  direction: 'prev' | 'next',
  index: number,
  publicationBookings: PublicationBookingModel[],
  selectPublicationBookingId: UsePublicationBookingPlannerPayload['callbacks']['selectPublicationBookingId'],
}

const renderPageNavigationButton = (params: RenderPageNavigationButtonParams) => {
  const {
    direction, index, publicationBookings, selectPublicationBookingId,
  } = params

  const indexModifier = direction === 'prev' ? -1 : 1

  const bookingAtIndex = publicationBookings[index + indexModifier]
  if (bookingAtIndex){
    const { id, publication_id, publication_edition_id } = bookingAtIndex

    return (
      <a
        className={classnames(styles.navigation, styles[direction === 'prev' ? 'prev' : 'next'])}
        // @ts-expect-error TS2345
        onClick={() => selectPublicationBookingId({
          entityParams: {
            id,
            publication_edition_id,
            publication_id,
          },
        })}
      >
        <FontAwesomeIcon icon={direction === 'prev' ? faAngleLeft : faAngleRight} />
      </a>
    )
  }

  return null
}

interface CanvasProps {
  bookingPlannerPayload: UsePublicationBookingPlannerPayload,
}

const Canvas = (props: CanvasProps) => {
  const {
    bookingPlannerPayload,
    bookingPlannerPayload: {
      callbacks: {
        showCreateOrEditPublicationBookingModal,
        selectPublicationBookingId,
      },
      editionData: {
        isBooked,
        isPending,
      },
      publicationBookingPayload: {
        layoutTemplate,
      },
      publicationBookingsPayload: {
        filteredPublicationBookings,
      },
      selectedBookingIndex,
      zoom,
    },
  } = props

  const canvasPayload = useCanvas({
    layoutTemplate,
    zoom,
  })

  const {
    canvasStyle,
  } = canvasPayload

  return (
    <Box
      background-color="white"
      className={styles.root}
      flexShrink={0}
      margin="0 auto"
      position="relative"
      // @ts-expect-error TS2322
      style={canvasStyle}
    >
      {/* Navigation */}
      {renderPageNavigationButton({
        direction: 'prev',
        index: selectedBookingIndex,
        publicationBookings: filteredPublicationBookings,
        selectPublicationBookingId,
      })}

      {renderPageNavigationButton({
        direction: 'next',
        index: selectedBookingIndex,
        publicationBookings: filteredPublicationBookings,
        selectPublicationBookingId,
      })}

      {renderAddPageButton({
        index: selectedBookingIndex,
        isBooked,
        isPending,
        publicationBookings: filteredPublicationBookings,
        showCreateOrEditPublicationBookingModal,
      })}
      {/* End Navigation */}

      <ContentGrid bookingPlannerPayload={bookingPlannerPayload} />
    </Box>
  )
}

export default Canvas
